import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { useCommunity } from "@/features/communities/hooks/useCommunity"
import { getOtherProfile } from "@/features/profile/services/user.service"
import { useRitual } from "@/features/rituals/hooks/useRitual"
import { transformRitualDetailToUI } from "@/features/rituals/services/ritual.service"

interface DetailPageDataResult {
  communityData: ReturnType<typeof useCommunity>["data"]
  ritualData: ReturnType<typeof useRitual>["data"]
  ritualUIData: ReturnType<typeof transformRitualDetailToUI> | undefined
  otherProfileData:
    | { user: { id: string; name?: string; username?: string; profilePictureUrl?: string } }
    | undefined
}

interface DetailPageDataParams {
  isCommunityDetail: boolean
  isRitualDetail: boolean
  isOtherProfilePage: boolean
  communityId: string | undefined
  ritualId: string | undefined
  otherProfileUserId: string | undefined
}

export function useDetailPageData({
  isCommunityDetail,
  isRitualDetail,
  isOtherProfilePage,
  communityId,
  ritualId,
  otherProfileUserId,
}: DetailPageDataParams): DetailPageDataResult {
  const { data: communityData } = useCommunity(communityId || "", {
    enabled: isCommunityDetail && !!communityId,
  })

  const { data: ritualData } = useRitual(ritualId || "", {
    enabled: isRitualDetail && !!ritualId,
  })

  const ritualUIData = useMemo(() => {
    if (!ritualData) return undefined
    return transformRitualDetailToUI(ritualData)
  }, [ritualData])

  const { data: otherProfileData } = useQuery({
    queryKey: ["profile", otherProfileUserId],
    queryFn: () => getOtherProfile(otherProfileUserId || ""),
    enabled: isOtherProfilePage && !!otherProfileUserId,
  })

  return {
    communityData,
    ritualData,
    ritualUIData,
    otherProfileData,
  }
}
